import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from '@/components/ui/select';
import {
	Calendar,
	ClipboardList,
	Flag,
	Link as LinkIcon,
	User,
	Users,
	Hash,
	Plus,
	Type,
	CheckSquare,
	Settings,
	Trash2,
	Edit2,
	Check,
	X,
	Star,
	MessageSquare,
	Tag,
	MoreHorizontal,
	ArrowRight,
	CheckCircle,
	Clock,
} from 'lucide-react';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import moment from 'moment';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { PipelineItem, Pipeline } from '@/reducers/pipelineReducer';
import type { RootState } from '@/reducers';
import {
	addPropertyDefinition,
	updateItemCustomField,
	deletePropertyDefinition,
	updatePropertyDefinition,
	updateItem,
	moveItem,
	markItemSeen,
} from '@/actions/pipeline';
import PropertyCell from './PropertyCell';
import AddPropertyDialog from './AddPropertyDialog';
import CustomerLookup from '@/components/webchat/chat/CustomerLookup';
import { CustomerNotes } from '@/components/webchat/chat/ConversationDetails/CustomerNotes';
import CustomerQuestionnaireDialog from '@/components/webchat/chat/Chatfeed/CustomerQuestionnaireDialog';
import { PIPELINE_ACTIONS } from '@/actions/pipeline';
import formatPhoneForUser from '@/utils/formatPhoneForUser';
import { usePhoneMask } from '@/hooks/usePhoneMask';
import copyTextToClipboard from '@/utils/copyTextToClipboard';
import { buildPipelineItemPath } from '@/utils/pipelineItemRoutes';
import {
	isItemOverdue,
	PRIORITY_OPTIONS,
	getPriorityChip,
	type PipelinePriority,
} from './utils/itemDisplay';
import {
	goToCustomerConversation,
	extractCustomerId,
} from '@/utils/goToCustomerConversation';
import type { Customer } from '@/types';
import {
	ActionSheet,
	MobileBackButton,
	MobileHeader,
	MobileHeaderButton,
	useHideMobileTabBar,
	type ActionSheetItem,
} from '@/components/mobile';
import { isTouchViewport, useIsMobile } from '@/hooks/use-mobile';
import { moveToColumnItems } from './utils/moveToColumn';

interface PipelineItemSheetProps {
	item: PipelineItem | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	pipelineId?: string;
}

const PipelineItemSheet: React.FC<PipelineItemSheetProps> = ({
	item: initialItem,
	open,
	onOpenChange,
	pipelineId,
}) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const maskPhone = usePhoneMask();
	const [showAddProperty, setShowAddProperty] = useState(false);
	const [deletePropertyId, setDeletePropertyId] = useState<string | null>(
		null
	);
	const [deletePropertyName, setDeletePropertyName] = useState<string>('');
	const [editPropertyId, setEditPropertyId] = useState<string | null>(null);
	const [editPropertyName, setEditPropertyName] = useState<string>('');
	const [editPropertyNewName, setEditPropertyNewName] = useState<string>('');
	const [hoveredPropertyId, setHoveredPropertyId] = useState<string | null>(
		null
	);
	const [isFetchingConversation, setIsFetchingConversation] = useState(false);

	// States for inline editing
	const [isEditingName, setIsEditingName] = useState(false);
	const [isEditingDescription, setIsEditingDescription] = useState(false);
	const [editedName, setEditedName] = useState('');
	const [editedDescription, setEditedDescription] = useState('');
	const [isNameHovered, setIsNameHovered] = useState(false);
	const [isDescriptionHovered, setIsDescriptionHovered] = useState(false);

	// Tags state
	const [tagInput, setTagInput] = useState('');
	const [isAddingTag, setIsAddingTag] = useState(false);
	const [showTagSuggestions, setShowTagSuggestions] = useState(false);
	const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
	const tagInputRef = useRef<HTMLInputElement>(null);
	const [dropdownPosition, setDropdownPosition] = useState({
		top: 0,
		left: 0,
		width: 0,
	});

	// Customer dialog state
	const [customerDialogIsOpen, setCustomerDialogIsOpen] = useState(false);

	// Form-submission viewer state (items auto-created from a form submission
	// link back to the QuestionnaireRequest they came from)
	const [formSubmissionOpen, setFormSubmissionOpen] = useState(false);

	// Always get the latest item from Redux to ensure we have current data
	const item = useSelector((state: any) => {
		if (!initialItem?._id) return initialItem;

		const pid = pipelineId || initialItem._pipeline;
		const items = state.pipeline.items[pid] || [];
		const currentItem = items.find(
			(i: PipelineItem) => i._id === initialItem._id
		);

		// Return the current item from Redux if found, otherwise use the initial item
		return currentItem || initialItem;
	});

	// Set when this item was auto-created from a form submission — the linked
	// QuestionnaireRequest lets us show the structured answers.
	const formSubmissionRequestId = item?.linkedEntities?.find(
		(entity) => entity.entityType === 'form_submission'
	)?._entity;

	// Get the current pipeline from Redux store
	const pipeline = useSelector((state: any) => {
		const pid = pipelineId || item?._pipeline;
		// First check if currentPipeline matches
		if (state.pipeline.currentPipeline?._id === pid) {
			return state.pipeline.currentPipeline;
		}
		// Otherwise look in the pipelines array
		return state.pipeline.pipelines.find((p: Pipeline) => p._id === pid);
	});

	// Get all items for this pipeline from Redux store
	const allPipelineItems = useSelector((state: any) => {
		const pid = pipelineId || item?._pipeline;
		return state.pipeline.items[pid] || [];
	});

	// Get users from Redux store
	const users = useSelector((state: RootState) => state.users);

	// Current user, used to mark this item as seen when the sheet is opened.
	const currentUserId = useSelector(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(state: any) => state.auth?.user?._id as string | undefined
	);

	const reduxTags = useSelector((state: RootState) => state.tags);

	// Filter and sort tag suggestions based on input
	const tagSuggestions = Array.isArray(reduxTags)
		? reduxTags
				.filter((tag: string) => {
					// Filter out null/undefined values
					if (!tag || typeof tag !== 'string') return false;

					// Don't suggest tags that are already added
					if (item?.tags?.includes(tag)) return false;

					const searchTerm = tagInput.toLowerCase().trim();
					// Show all tags if no search term, otherwise filter
					if (!searchTerm) return true;
					return tag.toLowerCase().includes(searchTerm);
				})
				.sort((a, b) => {
					// Safety check for null/undefined values
					if (!a || !b) return 0;
					return a.toLowerCase().localeCompare(b.toLowerCase());
				})
		: [];

	// Update dropdown position when showing suggestions
	useEffect(() => {
		if (showTagSuggestions && tagInputRef.current) {
			const rect = tagInputRef.current.getBoundingClientRect();
			const dropdownHeight = 400; // Max height of dropdown
			const spaceBelow = window.innerHeight - rect.bottom;
			const shouldShowAbove =
				spaceBelow < dropdownHeight && rect.top > dropdownHeight;

			setDropdownPosition({
				top: shouldShowAbove
					? rect.top + window.scrollY - dropdownHeight
					: rect.bottom + window.scrollY,
				left: rect.left + window.scrollX,
				width: rect.width,
			});
		}
	}, [showTagSuggestions]);

	// Phones show the item as a full-screen pushed page with its own header.
	const isMobile = useIsMobile();
	const [phoneActionsOpen, setPhoneActionsOpen] = useState(false);
	useHideMobileTabBar(open && isMobile);

	// Mark the item as seen by the current user when the sheet is opened.
	// The inner guard prevents re-dispatching once the item is marked seen.
	useEffect(() => {
		if (
			open &&
			item?._id &&
			currentUserId &&
			!item._seenBy?.includes(currentUserId)
		) {
			const pid = pipelineId || item._pipeline;
			dispatch(markItemSeen(item._id, pid) as any);
		}
	}, [open, item, currentUserId, pipelineId, dispatch]);

	if (!item) return null;

	// Format date helper
	const formatDate = (date: Date | string | null | undefined) => {
		if (!date) return 'Not set';
		const d = new Date(date);
		return moment(d).format('dddd, ll, LT');
	};

	// Touch screens can't hover, so the edit buttons stay visible there.
	const touchUi = isTouchViewport();

	const copyItemLink = () => {
		if (item.itemNumber == null) return;
		copyTextToClipboard(
			`${window.location.origin}${buildPipelineItemPath({
				itemNumber: item.itemNumber as number,
			})}`
		);
		toast.success('Link copied');
	};

	// Handle property deletion
	const handlePropertyDelete = async () => {
		if (deletePropertyId && pipeline) {
			await dispatch(
				deletePropertyDefinition(pipeline._id, deletePropertyId) as any
			);
			setDeletePropertyId(null);
			setDeletePropertyName('');
		}
	};

	// Handle property editing
	const handlePropertyEdit = async () => {
		if (editPropertyId && editPropertyNewName.trim() && pipeline) {
			const result = await dispatch(
				updatePropertyDefinition(pipeline._id, editPropertyId, {
					name: editPropertyNewName.trim(),
				}) as any
			);
			if (result.success) {
				setEditPropertyId(null);
				setEditPropertyName('');
				setEditPropertyNewName('');
			}
		}
	};

	// Handle toggling property starred status
	const handleTogglePropertyStar = async (
		propertyId: string,
		currentStarred: boolean
	) => {
		if (pipeline) {
			await dispatch(
				updatePropertyDefinition(pipeline._id, propertyId, {
					isStarred: !currentStarred,
				}) as any
			);
		}
	};

	// Handle status/column change
	const handleStatusChange = async (newColumnId: string) => {
		if (!item || !pipeline || newColumnId === item.columnId) return;

		// Find the number of items in the target column to position at the bottom
		const itemsInTargetColumn = allPipelineItems.filter(
			(i: PipelineItem) =>
				i.columnId === newColumnId && i.status !== 'archived'
		).length;

		const result = await dispatch(
			moveItem(
				item._id,
				pipeline._id,
				item.columnId,
				newColumnId,
				itemsInTargetColumn
			) as any
		);

		// Surface the automatic stage-change text, if one was queued
		if (result?.stageSms?.queued) {
			toast.success('Text message sent', {
				description: result.stageSms.customerName
					? `Sent to ${result.stageSms.customerName}`
					: 'The customer was notified',
			});
		}
	};

	// Handle assignee change
	const handleAssigneeChange = async (userId: string) => {
		if (!item || !pipeline) return;

		// Use empty string to represent unassigned (will be converted to null on backend)
		const assigneeValue = userId === 'unassigned' ? null : userId;

		await dispatch(
			updateItem(item._id, pipeline._id, {
				_assignee: assigneeValue,
			}) as any
		);
	};

	// Handle due date change (null clears the date)
	const handleDueDateChange = async (date: Date | null) => {
		if (!item || !pipeline) return;

		await dispatch(
			updateItem(item._id, pipeline._id, {
				dueDate: date,
			}) as any
		);
	};

	// Handle priority change
	const handlePriorityChange = async (priority: string) => {
		if (!item || !pipeline) return;

		await dispatch(
			updateItem(item._id, pipeline._id, {
				priority: priority as PipelinePriority,
			}) as any
		);
	};

	// Handle customer selection/creation from CustomerLookup
	const handleSaveCustomer = async (customerData: {
		name?: string;
		phoneEmail?: string;
		phone?: string;
		email?: string;
		details?: string;
		_location?: string;
		_id?: string; // If it's an existing customer
	}) => {
		if (!item || !pipeline) return;

		try {
			let customerId = customerData._id;
			let customerObject: any = customerData;

			// If it's a new customer (no _id), we need to create it first
			if (!customerId && customerData.phoneEmail) {
				// Create the new customer
				const createResponse = await axios.post(
					'/api/create-customer',
					{
						customer: {
							name: customerData.name || '',
							phoneEmail: customerData.phoneEmail, // Backend expects phoneEmail field
							details: customerData.details,
							_location: customerData._location,
						},
					}
				);

				if (createResponse.data?.customer) {
					customerId = createResponse.data.customer._id;
					customerObject = createResponse.data.customer;
				}
			} else if (customerId) {
				// For existing customers, build a proper customer object
				// CustomerLookup may pass either phoneEmail or separate phone/email
				const phone =
					customerData.phone ||
					(!customerData.phoneEmail?.includes('@')
						? customerData.phoneEmail
						: null);
				const email =
					customerData.email ||
					(customerData.phoneEmail?.includes('@')
						? customerData.phoneEmail
						: null);

				customerObject = {
					_id: customerId,
					name: customerData.name || '',
					phone: phone,
					email: email,
					details: customerData.details,
					_location: customerData._location,
				};
			}

			if (!customerId) return;

			// First, do an optimistic update with the full customer object
			// This will show the customer name immediately
			dispatch({
				type: PIPELINE_ACTIONS.UPDATE_ITEM,
				payload: {
					pipelineId: pipeline._id,
					item: {
						...item,
						_customer: customerObject, // Full object for immediate display
					},
				},
			});

			// Then send the actual update to the backend with just the ID
			// The backend will save the ID and return the populated object
			const response = await axios.put(`/api/pipeline/item/${item._id}`, {
				_customer: customerId, // Send just the ID to backend
			});

			if (response.data.item && !response.data.error) {
				// Update with the populated item from the backend
				dispatch({
					type: PIPELINE_ACTIONS.UPDATE_ITEM,
					payload: {
						pipelineId: pipeline._id,
						item: response.data.item,
					},
				});
			}

			// Close the dialog after successful save
			setCustomerDialogIsOpen(false);
		} catch (error) {
			console.error('Error saving/attaching customer:', error);
			// On error, revert to original item
			dispatch({
				type: PIPELINE_ACTIONS.UPDATE_ITEM,
				payload: {
					pipelineId: pipeline._id,
					item: item,
				},
			});
			setCustomerDialogIsOpen(false);
		}
	};

	// Handle customer clear
	const handleCustomerClear = async () => {
		if (!item || !pipeline) return;

		await dispatch(
			updateItem(item._id, pipeline._id, {
				_customer: null,
			}) as any
		);
	};

	// Navigate to customer conversation
	const goToConversation = async () => {
		const customerId = extractCustomerId(item?._customer);
		if (!customerId) return;

		setIsFetchingConversation(true);
		try {
			await goToCustomerConversation({
				customerId,
				navigate,
			});
		} catch {
			// Error already handled by utility function
		} finally {
			setIsFetchingConversation(false);
		}
	};

	// Phone "…" menu on the item page
	const handleStatusToggle = async () => {
		if (!item || !pipeline) return;
		const result = await dispatch(
			updateItem(item._id, pipeline._id, {
				status: item.status === 'completed' ? 'active' : 'completed',
			}) as any
		);
		if (result?.reviewRequest?.queued) {
			toast.success('Review request sent', {
				description: result.reviewRequest.customerName
					? `Sent to ${result.reviewRequest.customerName}`
					: 'The customer was invited to leave a review',
			});
		}
	};
	const phoneActionSections: { items: ActionSheetItem[] }[] = [
		{
			items: [
				{ id: 'move', label: 'Move to…', icon: ArrowRight, page: 'move' },
				item.status === 'completed'
					? { id: 'status', label: 'Mark active', icon: Clock, onSelect: handleStatusToggle }
					: { id: 'status', label: 'Mark complete', icon: CheckCircle, onSelect: handleStatusToggle },
				...(extractCustomerId(item._customer)
					? [
							{
								id: 'conversation',
								label: 'Open conversation',
								icon: MessageSquare,
								disabled: isFetchingConversation,
								onSelect: goToConversation,
							},
						]
					: []),
				...(item.itemNumber != null
					? [{ id: 'link', label: 'Copy link', icon: LinkIcon, onSelect: copyItemLink }]
					: []),
			],
		},
	];

	// Handle name editing
	const handleStartEditName = () => {
		setEditedName(item.name);
		setIsEditingName(true);
	};

	const handleSaveName = async () => {
		if (editedName.trim() && editedName !== item.name) {
			const result = await dispatch(
				updateItem(item._id, item._pipeline, {
					name: editedName.trim(),
				}) as any
			);
			if (result.success) {
				setIsEditingName(false);
				setEditedName('');
			}
		} else {
			setIsEditingName(false);
			setEditedName('');
		}
	};

	const handleCancelEditName = () => {
		setIsEditingName(false);
		setEditedName('');
	};

	// Handle description editing
	const handleStartEditDescription = () => {
		setEditedDescription(item.description || '');
		setIsEditingDescription(true);
	};

	const handleSaveDescription = async () => {
		if (editedDescription !== item.description) {
			const result = await dispatch(
				updateItem(item._id, item._pipeline, {
					description: editedDescription.trim(),
				}) as any
			);
			if (result.success) {
				setIsEditingDescription(false);
				setEditedDescription('');
			}
		} else {
			setIsEditingDescription(false);
			setEditedDescription('');
		}
	};

	const handleCancelEditDescription = () => {
		setIsEditingDescription(false);
		setEditedDescription('');
	};

	// Tag handlers
	const handleAddTag = async (tagToAdd?: string) => {
		if (!item?._id || !item?._pipeline) return;

		// Use provided tag or the current input
		const newTag = (tagToAdd || tagInput).trim().substring(0, 30);
		if (!newTag) return;

		// Check if tag already exists
		if (item.tags && item.tags.includes(newTag)) {
			setTagInput('');
			setShowTagSuggestions(false);
			return;
		}

		const updatedTags = [...(item.tags || []), newTag];

		// Update the item with new tags
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const result = await dispatch(
			updateItem(item._id, item._pipeline, { tags: updatedTags }) as any
		);

		if (result.success) {
			setTagInput('');
			setIsAddingTag(false);
			setShowTagSuggestions(false);
			setSelectedSuggestionIndex(-1);
		}
	};

	const handleRemoveTag = async (tagToRemove: string) => {
		if (!item?._id || !item?._pipeline) return;

		const updatedTags = (item.tags || []).filter(
			(tag) => tag !== tagToRemove
		);

		// Update the item with removed tag
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		await dispatch(
			updateItem(item._id, item._pipeline, { tags: updatedTags }) as any
		);
	};

	const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setTagInput(value);
		// Always show suggestions when typing
		setShowTagSuggestions(true);
		setSelectedSuggestionIndex(-1);
	};

	const handleTagInputKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			if (
				selectedSuggestionIndex >= 0 &&
				tagSuggestions[selectedSuggestionIndex]
			) {
				// Add the selected suggestion
				handleAddTag(tagSuggestions[selectedSuggestionIndex]);
			} else {
				// Add the typed tag
				handleAddTag();
			}
		} else if (e.key === 'Escape') {
			setTagInput('');
			setIsAddingTag(false);
			setShowTagSuggestions(false);
			setSelectedSuggestionIndex(-1);
		} else if (e.key === 'ArrowDown') {
			e.preventDefault();
			setSelectedSuggestionIndex((prev) =>
				prev < tagSuggestions.length - 1 ? prev + 1 : prev
			);
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
		}
	};

	const handleSuggestionClick = (tag: string) => {
		handleAddTag(tag);
	};

	return (
		<>
			<Sheet open={open} onOpenChange={onOpenChange}>
				<SheetContent
					className="sm:max-w-[800px] overflow-y-auto max-md:p-0 max-md:outline-none"
					hideClose={isMobile}
					safeArea={false}
					onPointerDownOutside={(e) => {
						// Check if the click target is part of the react-select portal or tag dropdown
						const target = e.target as HTMLElement;
						if (
							target.closest('[class*="react-select"]') ||
							target.closest('[data-tag-dropdown="true"]')
						) {
							e.preventDefault();
						}
					}}
					onInteractOutside={(e) => {
						// Also prevent for onInteractOutside
						const target = e.target as HTMLElement;
						if (
							target.closest('[class*="react-select"]') ||
							target.closest('[data-tag-dropdown="true"]')
						) {
							e.preventDefault();
						}
					}}
					onFocusOutside={(e) => {
						// Prevent focus outside events for react-select and tag dropdown
						const target = e.target as HTMLElement;
						if (
							target.closest('[class*="react-select"]') ||
							target.closest('[data-tag-dropdown="true"]')
						) {
							e.preventDefault();
						}
					}}
					onEscapeKeyDown={(e) => {
						// Check if react-select dropdown or tag dropdown is open
						if (
							document.querySelector(
								'[class*="react-select__menu"]'
							) ||
							document.querySelector('[data-tag-dropdown="true"]')
						) {
							e.preventDefault();
						}
					}}
				>
					{isMobile && (
						<MobileHeader
							className="pt-safe"
							left={
								<MobileBackButton
									label="Pipeline"
									onClick={() => onOpenChange(false)}
								/>
							}
							title={
								item.itemNumber != null
									? `#${item.itemNumber}`
									: 'Card'
							}
							subtitle={
								pipeline?.columns?.find(
									(col) => col._id === item.columnId
								)?.name
							}
							right={
								<>
									{item.itemNumber != null && (
										<MobileHeaderButton
											aria-label="Copy link to this card"
											onClick={copyItemLink}
										>
											<LinkIcon />
										</MobileHeaderButton>
									)}
									<MobileHeaderButton
										aria-label="Card actions"
										aria-haspopup="dialog"
										onClick={() => setPhoneActionsOpen(true)}
									>
										<MoreHorizontal />
									</MobileHeaderButton>
								</>
							}
						/>
					)}
					<SheetHeader className="max-md:px-4 max-md:pt-4 max-md:text-left">
						{/* Ticket number + shareable link */}
						{item.itemNumber != null && (
							<div className="flex items-center gap-1 max-md:hidden">
								<span className="text-sm font-medium text-muted-foreground">
									#{item.itemNumber}
								</span>
								<Button
									size="icon"
									variant="ghost"
									className="h-6 w-6 text-muted-foreground hover:text-foreground touch:-my-2.5 touch:size-11"
									title="Copy link to this item"
									onClick={() => {
										copyTextToClipboard(
											`${
												window.location.origin
											}${buildPipelineItemPath({
												itemNumber: item.itemNumber as number,
											})}`
										);
										toast.success('Link copied');
									}}
								>
									<LinkIcon className="h-3.5 w-3.5" />
								</Button>
							</div>
						)}
						{/* Name with inline editing */}
						<div
							className="group"
							onMouseEnter={() => setIsNameHovered(true)}
							onMouseLeave={() => setIsNameHovered(false)}
						>
							{isEditingName ? (
								<div className="flex flex-col gap-2 w-full">
									<Input
										value={editedName}
										onChange={(e) =>
											setEditedName(e.target.value)
										}
										onKeyDown={(e) => {
											if (e.key === 'Enter')
												handleSaveName();
											if (e.key === 'Escape')
												handleCancelEditName();
										}}
										className="text-2xl font-bold border-2 focus:ring-2"
										aria-label="Name"
										autoCapitalize="sentences"
										enterKeyHint="done"
										autoFocus
									/>
									<div className="flex gap-2">
										<Button
											size="sm"
											variant="default"
											onClick={handleSaveName}
											className="max-md:h-11 max-md:px-4"
										>
											<Check className="h-4 w-4 mr-1" />
											Save
										</Button>
										<Button
											size="sm"
											variant="outline"
											onClick={handleCancelEditName}
											className="max-md:h-11 max-md:px-4"
										>
											<X className="h-4 w-4 mr-1" />
											Cancel
										</Button>
									</div>
								</div>
							) : (
								<div className="flex items-center gap-2">
									<SheetTitle className="text-2xl font-bold max-md:min-w-0 max-md:break-words">
										{item.name}
									</SheetTitle>
									{(isNameHovered || touchUi) && (
										<Button
											size="icon"
											variant="ghost"
											onClick={handleStartEditName}
											aria-label="Edit name"
											className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity touch:opacity-100 max-md:-my-2.5 max-md:size-11 touch:-my-2.5 touch:size-11 shrink-0" // DON'T CHANGE THIS HEIGHT AND WIDTH
										>
											<Edit2 className="h-2 w-2" />
										</Button>
									)}
								</div>
							)}
						</div>

						{/* Description with inline editing */}
						<div
							className="group mt-2"
							onMouseEnter={() => setIsDescriptionHovered(true)}
							onMouseLeave={() => setIsDescriptionHovered(false)}
						>
							{isEditingDescription ? (
								<div className="flex flex-col gap-2 w-full">
									<Textarea
										value={editedDescription}
										onChange={(e) =>
											setEditedDescription(e.target.value)
										}
										onKeyDown={(e) => {
											if (e.key === 'Escape')
												handleCancelEditDescription();
										}}
										className="text-base resize-none"
										aria-label="Description"
										autoCapitalize="sentences"
										rows={3}
										autoFocus
										placeholder="Add a description..."
									/>
									<div className="flex gap-2">
										<Button
											size="sm"
											variant="default"
											onClick={handleSaveDescription}
											className="max-md:h-11 max-md:px-4"
										>
											<Check className="h-4 w-4 mr-1" />
											Save
										</Button>
										<Button
											size="sm"
											variant="outline"
											onClick={
												handleCancelEditDescription
											}
											className="max-md:h-11 max-md:px-4"
										>
											<X className="h-4 w-4 mr-1" />
											Cancel
										</Button>
									</div>
								</div>
							) : (
								<div className="flex items-start gap-2">
									{item.description ? (
										<>
											<SheetDescription className="text-base">
												{item.description}
											</SheetDescription>
											{(isDescriptionHovered || touchUi) && (
												<Button
													size="icon"
													variant="ghost"
													onClick={
														handleStartEditDescription
													}
													aria-label="Edit description"
													className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 touch:opacity-100 max-md:-my-2.5 max-md:size-11 touch:-my-2.5 touch:size-11" // DON'T CHANGE THIS HEIGHT AND WIDTH
												>
													<Edit2 className="h-4 w-4" />
												</Button>
											)}
										</>
									) : (
										<>
											<SheetDescription className="text-base text-muted-foreground/70 italic">
												No description
											</SheetDescription>
											{(isDescriptionHovered || touchUi) && (
												<Button
													size="icon"
													variant="ghost"
													onClick={
														handleStartEditDescription
													}
													aria-label="Edit description"
													className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 touch:opacity-100 max-md:-my-2.5 max-md:size-11 touch:-my-2.5 touch:size-11"
												>
													<Edit2 className="h-2 w-2" />
												</Button>
											)}
										</>
									)}
								</div>
							)}
						</div>

						{/* Structured answers for items created from a form submission */}
						{formSubmissionRequestId && (
							<div className="mt-2">
								<Button
									variant="outline"
									size="sm"
									className="gap-1.5"
									onClick={() => setFormSubmissionOpen(true)}
								>
									<ClipboardList className="h-3.5 w-3.5" />
									View form submission
								</Button>
							</div>
						)}
					</SheetHeader>

					<div className="mt-6 space-y-6 max-md:mt-5 max-md:px-4 max-md:pb-[max(1.5rem,var(--safe-bottom))]">
						{/* Built-in Properties in Excel-like style */}
						<div className="border border-border rounded-lg overflow-hidden">
							{/* Status Row */}
							<div className="flex items-stretch border-b border-border">
								<div className="flex items-center px-3 py-2 bg-muted/50 border-r border-border min-w-[150px] w-[40%] max-md:min-w-0 max-md:w-[38%]">
									<div className="w-3.5 h-3.5 mr-2 rounded-full bg-muted-foreground/40" />
									<span className="text-sm text-foreground/80">
										Status
									</span>
								</div>
								<div className="flex-1 px-3 py-1.5">
									<Select
										value={item.columnId}
										onValueChange={handleStatusChange}
									>
										<SelectTrigger className="h-8 border-0 shadow-none focus:ring-0 px-0 max-md:h-11 touch:-my-1.5 touch:h-11">
											<div className="flex items-center gap-2">
												<div
													className="w-3 h-3 rounded-full flex-shrink-0"
													style={{
														backgroundColor:
															pipeline?.columns?.find(
																(col) =>
																	col._id ===
																	item.columnId
															)?.color ||
															'#e2e8f0',
													}}
												/>
												<span className="text-sm">
													{pipeline?.columns?.find(
														(col) =>
															col._id ===
															item.columnId
													)?.name || 'Unknown'}
												</span>
											</div>
										</SelectTrigger>
										<SelectContent>
											{pipeline?.columns?.map(
												(column) => (
													<SelectItem
														key={column._id}
														value={column._id}
													>
														<div className="flex items-center gap-2">
															<div
																className="w-3 h-3 rounded-full"
																style={{
																	backgroundColor:
																		column.color ||
																		'#e2e8f0',
																}}
															/>
															{column.name}
														</div>
													</SelectItem>
												)
											)}
										</SelectContent>
									</Select>
								</div>
							</div>

							{/* Priority Row */}
							<div className="flex items-stretch border-b border-border">
								<div className="flex items-center px-3 py-2 bg-muted/50 border-r border-border min-w-[150px] w-[40%] max-md:min-w-0 max-md:w-[38%]">
									<Flag className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
									<span className="text-sm text-foreground/80">
										Priority
									</span>
								</div>
								<div className="flex-1 px-3 py-1.5">
									<Select
										value={item.priority || 'medium'}
										onValueChange={handlePriorityChange}
									>
										<SelectTrigger className="h-8 border-0 shadow-none focus:ring-0 px-0 max-md:h-11 touch:-my-1.5 touch:h-11">
											<div className="flex items-center gap-2">
												{(() => {
													const chip = getPriorityChip(
														item.priority
													);
													return chip ? (
														<Badge
															variant="outline"
															className={cn(
																'text-xs font-normal',
																chip.classes
															)}
														>
															{chip.label}
														</Badge>
													) : (
														<span className="text-sm">
															Medium
														</span>
													);
												})()}
											</div>
										</SelectTrigger>
										<SelectContent>
											{PRIORITY_OPTIONS.map((option) => (
												<SelectItem
													key={option.value}
													value={option.value}
												>
													{option.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>

							{/* Customer Row */}
							<div className="flex items-stretch border-b border-border">
								<div className="flex items-center px-3 py-2 bg-muted/50 border-r border-border min-w-[150px] w-[40%] max-md:min-w-0 max-md:w-[38%]">
									<Users className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
									<span className="text-sm text-foreground/80">
										Customer
									</span>
								</div>
								<div className="flex-1 px-3 py-2">
									{item._customer ? (
										<div className="flex items-center justify-between">
											<div className="flex flex-col">
												<span className="text-sm">
													{(() => {
														// Handle different types of customer data
														if (
															typeof item._customer ===
																'object' &&
															item._customer !==
																null
														) {
															// Customer is populated object
															return (
																maskPhone(
																	(item._customer as any).name,
																) ||
																'Unknown Customer'
															);
														} else if (
															typeof item._customer ===
															'string'
														) {
															// Customer is just an ID - shouldn't happen with our new approach
															return 'Loading...';
														}
														return 'Unknown Customer';
													})()}
												</span>
												{(() => {
													// Show phone or email if available
													if (
														typeof item._customer ===
															'object' &&
														item._customer !== null
													) {
														const customer =
															item._customer as any;
														if (customer.phone) {
															return (
																<span className="text-xs text-muted-foreground mt-0.5">
																	{maskPhone(
																		formatPhoneForUser(customer.phone),
																	)}
																</span>
															);
														} else if (
															customer.email
														) {
															return (
																<span className="text-xs text-muted-foreground mt-0.5">
																	{
																		customer.email
																	}
																</span>
															);
														}
													}
													return null;
												})()}
											</div>
											<div className="flex items-center gap-1">
												<Button
													onClick={goToConversation}
													disabled={
														isFetchingConversation
													}
													variant="ghost"
													size="sm"
													className="h-8 w-8 p-0 text-muted-foreground hover:text-blue-600 max-md:-my-1.5 max-md:size-11 touch:-my-1.5 touch:size-11"
													title="Open conversation"
													aria-label="Open conversation"
												>
													<MessageSquare className="w-4 h-4" />
												</Button>
												<Button
													onClick={
														handleCustomerClear
													}
													variant="ghost"
													size="sm"
													className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground max-md:-my-1.5 max-md:size-11 touch:-my-1.5 touch:size-11"
													title="Remove customer"
													aria-label="Remove customer"
												>
													<X className="w-4 h-4" />
												</Button>
											</div>
										</div>
									) : (
										<Button
											variant="outline"
											size="sm"
											className="justify-start font-normal hover:text-foreground"
											onClick={() =>
												setCustomerDialogIsOpen(true)
											}
										>
											<Plus className="w-3 h-3 mr-1" />
											Add customer
										</Button>
									)}
								</div>
							</div>

							{/* Assignee Row */}
							<div className="flex items-stretch border-b border-border">
								<div className="flex items-center px-3 py-2 bg-muted/50 border-r border-border min-w-[150px] w-[40%] max-md:min-w-0 max-md:w-[38%]">
									<User className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
									<span className="text-sm text-foreground/80">
										Assignee
									</span>
								</div>
								<div className="flex-1 px-3 py-1.5">
									<Select
										value={
											item._assignee
												? typeof item._assignee ===
												  'string'
													? item._assignee
													: item._assignee._id
												: 'unassigned'
										}
										onValueChange={handleAssigneeChange}
									>
										<SelectTrigger className="h-8 border-0 shadow-none focus:ring-0 px-0 max-md:h-11 touch:-my-1.5 touch:h-11">
											<div className="flex items-center gap-2">
												<span className="text-sm">
													{item._assignee
														? typeof item._assignee ===
														  'string'
															? users?.find(
																	(u: any) =>
																		u._id ===
																		item._assignee
															  )?.name ||
															  'Unknown User'
															: (
																	item._assignee as any
															  ).name ||
															  'Unknown User'
														: 'Unassigned'}
												</span>
											</div>
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="unassigned">
												<span className="text-muted-foreground">
													Unassigned
												</span>
											</SelectItem>
											{users?.map((user: any) => (
												<SelectItem
													key={user._id}
													value={user._id}
												>
													{user.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>

							{/* Due Date Row */}
							<div className="flex items-stretch border-b border-border">
								<div className="flex items-center px-3 py-2 bg-muted/50 border-r border-border min-w-[150px] w-[40%] max-md:min-w-0 max-md:w-[38%]">
									<Calendar className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
									<span className="text-sm text-foreground/80">
										Due date
									</span>
								</div>
								<div className="flex-1 px-3 py-1.5">
									<div className="flex items-center justify-between">
										{isMobile ? (
											<label
												className={cn(
													'relative flex h-10 min-w-0 flex-1 items-center text-[15px]',
													!item.dueDate &&
														'text-muted-foreground',
													isItemOverdue(item) &&
														'text-red-600 dark:text-red-400 font-medium'
												)}
											>
												<span className="truncate">
													{item.dueDate
														? moment(
																new Date(item.dueDate)
														  ).format('ll')
														: 'Add due date'}
													{isItemOverdue(item) && (
														<span className="ml-2 text-xs font-normal">
															Overdue
														</span>
													)}
												</span>
												{/* The native date picker, laid over the label */}
												<input
													type="date"
													aria-label="Due date"
													value={
														item.dueDate
															? moment(
																	new Date(item.dueDate)
															  ).format('YYYY-MM-DD')
															: ''
													}
													onChange={(e) =>
														handleDueDateChange(
															e.target.value
																? moment(
																		e.target.value,
																		'YYYY-MM-DD'
																  ).toDate()
																: null
														)
													}
													className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
												/>
											</label>
										) : (
										<Popover>
											<PopoverTrigger asChild>
												<button
													className={cn(
														'flex h-8 items-center text-sm hover:bg-accent rounded px-1 -ml-1 transition-colors touch:-my-1.5 touch:h-11',
														!item.dueDate &&
															'text-muted-foreground',
														isItemOverdue(item) &&
															'text-red-600 dark:text-red-400 font-medium'
													)}
												>
													{item.dueDate
														? moment(
																new Date(
																	item.dueDate
																),
														  ).format('ll')
														: 'Add due date'}
													{isItemOverdue(item) && (
														<span className="ml-2 text-xs font-normal">
															Overdue
														</span>
													)}
												</button>
											</PopoverTrigger>
											<PopoverContent
												className="w-auto p-0"
												align="start"
											>
												<CalendarComponent
													mode="single"
													selected={
														item.dueDate
															? new Date(
																	item.dueDate
															  )
															: undefined
													}
													onSelect={(date) =>
														handleDueDateChange(
															date || null
														)
													}
													initialFocus
												/>
											</PopoverContent>
										</Popover>
										)}
										{item.dueDate && (
											<Button
												onClick={() =>
													handleDueDateChange(null)
												}
												variant="ghost"
												size="sm"
												className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground max-md:-my-1.5 max-md:size-11 touch:-my-1.5 touch:size-11"
												title="Clear due date"
												aria-label="Clear due date"
											>
												<X className="w-4 h-4" />
											</Button>
										)}
									</div>
								</div>
							</div>

							{/* Tags Row */}
							<div className="flex items-stretch border-b border-border">
								<div className="flex items-center px-3 py-2 bg-muted/50 border-r border-border min-w-[150px] w-[40%] max-md:min-w-0 max-md:w-[38%]">
									<Tag className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
									<span className="text-sm text-foreground/80">
										Tags
									</span>
								</div>
								<div className="flex-1 px-3 py-2">
									<div className="flex flex-wrap gap-2 items-center">
										{/* Existing tags */}
										{item?.tags &&
											item.tags.map((tag, index) => (
												<Badge
													key={index}
													variant="secondary"
													className="flex items-center gap-1 pr-1 max-md:h-8 max-md:pl-3 max-md:pr-1.5 max-md:text-[13px]"
												>
													<span>{tag}</span>
													<button
														onClick={() =>
															handleRemoveTag(tag)
														}
														className="ml-1 rounded-full hover:bg-accent p-0.5 max-md:relative max-md:p-1 max-md:before:absolute max-md:before:-inset-2.5 touch:relative touch:before:absolute touch:before:-inset-3.5"
														title="Remove tag"
														aria-label={`Remove tag ${tag}`}
													>
														<X className="w-3 h-3" />
													</button>
												</Badge>
											))}

										{/* Add tag input */}
										{isAddingTag ? (
											<div className="relative flex items-center gap-1 max-md:w-full">
												<div className="relative max-md:min-w-0 max-md:flex-1">
													<Input
														ref={tagInputRef}
														type="text"
														value={tagInput}
														onChange={
															handleTagInputChange
														}
														onKeyDown={
															handleTagInputKeyPress
														}
														onBlur={() => {
															// Hide suggestions when focus is lost
															// Note: clicks on suggestions use onMouseDown with preventDefault
															// so they won't trigger this blur
															if (
																!tagInput.trim()
															) {
																setIsAddingTag(
																	false
																);
															}
															setShowTagSuggestions(
																false
															);
														}}
														onFocus={() => {
															// Always show suggestions on focus
															setShowTagSuggestions(
																true
															);
															setSelectedSuggestionIndex(
																-1
															);
														}}
														placeholder="Type tag..."
														aria-label="New tag"
														autoCapitalize="none"
														autoCorrect="off"
														enterKeyHint="done"
														className="h-7 w-40 text-sm max-md:h-10 max-md:w-full"
														maxLength={30}
														autoFocus
													/>
													{/* Dropdown rendered as portal below to avoid z-index issues */}
												</div>

												<Button
													size="sm"
													variant="ghost"
													onClick={() => {
														setTagInput('');
														setIsAddingTag(false);
														setShowTagSuggestions(
															false
														);
													}}
													className="h-7 w-7 p-0 max-md:size-11 touch:-my-2 touch:size-11"
													aria-label="Cancel adding tag"
												>
													<X className="w-3 h-3" />
												</Button>
											</div>
										) : (
											<Button
												size="sm"
												variant="outline"
												onClick={() =>
													setIsAddingTag(true)
												}
												className="h-7 text-xs max-md:h-11 max-md:px-3 max-md:text-[13px] touch:relative touch:before:absolute touch:before:-inset-2"
											>
												<Plus className="w-3 h-3 mr-1" />
												Add tag
											</Button>
										)}
										{/* Phones: suggestions inline under the field, clear of the keyboard */}
										{isMobile && isAddingTag && showTagSuggestions && (
											<div
												data-tag-dropdown="true"
												className="basis-full overflow-hidden rounded-lg border border-border bg-popover"
											>
												{tagSuggestions.length > 0 ? (
													<div className="max-h-48 overflow-y-auto overscroll-contain">
														{tagSuggestions.map((tag) => (
															<button
																key={tag}
																type="button"
																onMouseDown={(e) => e.preventDefault()}
																onClick={() => handleSuggestionClick(tag)}
																className="flex min-h-11 w-full items-center border-b border-border/60 px-3 text-left text-[15px] last:border-b-0 active:bg-accent"
															>
																{tag}
															</button>
														))}
													</div>
												) : (
													<div className="px-3 py-2.5 text-[13px] text-muted-foreground">
														{tagInput.trim()
															? `Tap Done to create “${tagInput.trim()}”`
															: 'No tags yet. Type to create your first tag.'}
													</div>
												)}
											</div>
										)}
									</div>
								</div>
							</div>
						</div>

						{/* Custom Properties Section */}
						<div>
							<h3 className="text-sm font-semibold text-muted-foreground mb-3">
								Properties
							</h3>

							<div className="border border-border rounded-lg overflow-hidden">
								{pipeline?.propertyDefinitions &&
								pipeline.propertyDefinitions.length > 0 ? (
									<>
										{pipeline.propertyDefinitions
											.sort(
												(a, b) =>
													a.position - b.position
											)
											.map((property) => (
												<div
													key={property.id}
													className="group flex items-stretch border-b border-border last:border-b-0 hover:bg-accent/50"
													onMouseEnter={() =>
														setHoveredPropertyId(
															property.id
														)
													}
													onMouseLeave={() =>
														setHoveredPropertyId(
															null
														)
													}
												>
													{/* Property Name Cell */}
													<div className="flex items-center px-3 py-2 bg-muted/50 border-r border-border min-w-[150px] w-[40%] max-md:min-w-0 max-md:w-[38%] relative">
														<DropdownMenu>
															<DropdownMenuTrigger
																asChild
															>
																<Button
																	variant="ghost"
																	size="sm"
																	className="mr-2 h-6 w-6 p-0 transition-all max-md:-my-2.5 max-md:-ml-2.5 max-md:mr-0 max-md:size-11 max-md:shrink-0 touch:-my-2.5 touch:-ml-2.5 touch:mr-0 touch:size-11 touch:shrink-0"
																	aria-label={`Options for ${property.name}`}
																	onClick={(
																		e
																	) =>
																		e.stopPropagation()
																	}
																>
																	{hoveredPropertyId ===
																	property.id ? (
																		<Settings className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
																	) : (
																		<>
																			{property.type ===
																				'text' && (
																				<Type className="w-3.5 h-3.5 text-muted-foreground" />
																			)}
																			{property.type ===
																				'number' && (
																				<Hash className="w-3.5 h-3.5 text-muted-foreground" />
																			)}
																			{property.type ===
																				'date' && (
																				<Calendar className="w-3.5 h-3.5 text-muted-foreground" />
																			)}
																			{property.type ===
																				'checkbox' && (
																				<CheckSquare className="w-3.5 h-3.5 text-muted-foreground" />
																			)}
																		</>
																	)}
																</Button>
															</DropdownMenuTrigger>
															<DropdownMenuContent align="start">
																<DropdownMenuItem
																	className="cursor-pointer"
																	onClick={() =>
																		handleTogglePropertyStar(
																			property.id,
																			property.isStarred ||
																				false
																		)
																	}
																>
																	<Star
																		className={`w-4 h-4 mr-2 ${
																			property.isStarred
																				? 'fill-yellow-500 text-yellow-500'
																				: ''
																		}`}
																	/>
																	{property.isStarred
																		? 'Hide from card'
																		: 'Show on card'}
																</DropdownMenuItem>
																<DropdownMenuItem
																	className="cursor-pointer"
																	onClick={() => {
																		setEditPropertyId(
																			property.id
																		);
																		setEditPropertyName(
																			property.name
																		);
																		setEditPropertyNewName(
																			property.name
																		);
																	}}
																>
																	<Edit2 className="w-4 h-4 mr-2" />
																	Edit
																	Property
																</DropdownMenuItem>
																<DropdownMenuItem
																	className="text-red-600 focus:text-red-700 cursor-pointer"
																	onClick={() => {
																		setDeletePropertyId(
																			property.id
																		);
																		setDeletePropertyName(
																			property.name
																		);
																	}}
																>
																	<Trash2 className="w-4 h-4 mr-2" />
																	Delete
																	Property
																</DropdownMenuItem>
															</DropdownMenuContent>
														</DropdownMenu>
														<span className="text-sm text-foreground/80 flex items-center gap-1">
															{property.name}
															{property.isStarred && (
																<Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
															)}
														</span>
													</div>

													{/* Property Value Cell */}
													<div className="flex-1 flex items-center">
														<PropertyCell
															property={property}
															value={
																item
																	.customFields?.[
																	property.id
																]
															}
															onChange={async (
																value
															) => {
																await dispatch(
																	updateItemCustomField(
																		item._id,
																		property.id,
																		value
																	) as any
																);
															}}
															className="w-full"
														/>
													</div>
												</div>
											))}

										{/* Add Property Row */}
										<Button
											onClick={() =>
												setShowAddProperty(true)
											}
											variant="ghost"
											className="w-full justify-start px-3 py-2 h-auto font-normal hover:bg-accent max-md:min-h-11 touch:min-h-11"
										>
											<Plus className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
											<span className="text-sm text-muted-foreground">
												Add a property
											</span>
										</Button>
									</>
								) : (
									<Button
										onClick={() => setShowAddProperty(true)}
										variant="ghost"
										className="w-full justify-start px-3 py-3 h-auto font-normal hover:bg-accent max-md:min-h-11 touch:min-h-11"
									>
										<Plus className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
										<span className="text-sm text-muted-foreground">
											Add a property
										</span>
									</Button>
								)}
							</div>
						</div>

						{/* Customer Notes Section, only if the item has a customer */}
						{item._customer && (
							<div className="border-t pt-4">
								<CustomerNotes
									options={{
										isPipelineItem: true,
										hideOuterBorder: true,
									}}
									customerInfo={{
										_id: extractCustomerId(item._customer),
										name: (() => {
											if (
												typeof item._customer ===
													'object' &&
												item._customer !== null
											) {
												return (
													(item._customer as Customer)
														.name ||
													'Unknown Customer'
												);
											}
											return 'Unknown Customer';
										})(),
										_business: item._business,
									}}
								/>
							</div>
						)}

						{/* Metadata - Assignment and Timeline */}
						<div className="text-xs text-muted-foreground pt-4 border-t border-border flex justify-between max-md:flex-col max-md:gap-2">
							{/* Left: Timeline */}
							<div className="space-y-1">
								<div>Created {formatDate(item.createdAt)}</div>
								{item.updatedAt && (
									<div>
										Updated {formatDate(item.updatedAt)}
									</div>
								)}
							</div>

							{/* Right: Assignment Info */}
							{item._assignee && (
								<div className="text-right max-md:text-left">
									Assigned to{' '}
									{typeof item._assignee === 'string'
										? users?.find(
												(u: any) =>
													u._id === item._assignee
										  )?.name || 'Unknown'
										: (item._assignee as any).name ||
										  'Unknown'}
									{item._assignedBy && (
										<>
											{' '}
											by{' '}
											{typeof item._assignedBy ===
											'string'
												? users?.find(
														(u: any) =>
															u._id ===
															item._assignedBy
												  )?.name || 'Unknown'
												: (item._assignedBy as any)
														.name || 'Unknown'}
										</>
									)}
									{item.assignedAt && (
										<span>
											{' '}
											on {formatDate(item.assignedAt)}
										</span>
									)}
								</div>
							)}
						</div>
					</div>
					{isMobile && (
						<ActionSheet
							open={phoneActionsOpen}
							onOpenChange={setPhoneActionsOpen}
							title={item.name}
							sections={phoneActionSections}
							pages={[
								{
									id: 'move',
									title: 'Move to',
									sections: [
										{
											items: moveToColumnItems(
												pipeline?.columns || [],
												item.columnId,
												handleStatusChange
											),
										},
									],
								},
							]}
						/>
					)}
				</SheetContent>

				{/* Form submission viewer (mounts on open — it fetches on mount) */}
				{formSubmissionOpen && formSubmissionRequestId && (
					<CustomerQuestionnaireDialog
						_questionnaireRequest={formSubmissionRequestId}
						open
						closeDialog={() => setFormSubmissionOpen(false)}
					/>
				)}

				{/* Add Property Dialog */}
				<AddPropertyDialog
					open={showAddProperty}
					onOpenChange={setShowAddProperty}
					existingProperties={pipeline?.propertyDefinitions}
					onAdd={async (property) => {
						if (pipeline) {
							await dispatch(
								addPropertyDefinition(
									pipeline._id,
									property
								) as any
							);
						}
					}}
				/>

				{/* Delete Property Confirmation Dialog */}
				<Dialog
					open={!!deletePropertyId}
					onOpenChange={(open) => {
						if (!open) {
							setDeletePropertyId(null);
							setDeletePropertyName('');
						}
					}}
				>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Delete Property</DialogTitle>
							<DialogDescription>
								Are you sure you want to delete the property
								&ldquo;
								{deletePropertyName}&rdquo;?
								<br />
								<br />
								This will remove this property from{' '}
								<strong>all items</strong> in this pipeline. Any
								values stored in this property will be
								permanently deleted.
								<br />
								<br />
								This action cannot be undone.
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => {
									setDeletePropertyId(null);
									setDeletePropertyName('');
								}}
							>
								Cancel
							</Button>
							<Button
								onClick={handlePropertyDelete}
								className="bg-red-600 hover:bg-red-700 text-white"
							>
								Delete Property
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Edit Property Dialog */}
				<Dialog
					open={!!editPropertyId}
					onOpenChange={(open) => {
						if (!open) {
							setEditPropertyId(null);
							setEditPropertyName('');
							setEditPropertyNewName('');
						}
					}}
				>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Edit Property</DialogTitle>
							<DialogDescription className="space-y-4">
								<div>
									Enter a new name for the property &ldquo;
									{editPropertyName}&rdquo;:
								</div>
								<Input
									value={editPropertyNewName}
									onChange={(e) =>
										setEditPropertyNewName(e.target.value)
									}
									onKeyDown={(e) => {
										if (
											e.key === 'Enter' &&
											editPropertyNewName.trim()
										) {
											e.preventDefault();
											handlePropertyEdit();
										}
									}}
									placeholder="Property name"
									className="mt-2"
									autoFocus
								/>
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => {
									setEditPropertyId(null);
									setEditPropertyName('');
									setEditPropertyNewName('');
								}}
							>
								Cancel
							</Button>
							<Button
								onClick={handlePropertyEdit}
								disabled={
									!editPropertyNewName.trim() ||
									editPropertyNewName === editPropertyName
								}
							>
								Save
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Tag suggestions dropdown portal */}
				{!isMobile &&
					showTagSuggestions &&
					(tagSuggestions.length > 0 ||
						tagInput.trim() ||
						reduxTags.length === 0) &&
					createPortal(
						<>
							{/* Invisible backdrop to ensure dropdown is interactive */}
							<div
								className="fixed inset-0"
								style={{
									zIndex: 999998,
									pointerEvents: 'none',
								}}
							/>
							<div
								data-tag-dropdown="true"
								className="fixed bg-popover border border-border rounded-md shadow-lg max-h-[400px] overflow-y-auto cursor-default"
								style={{
									top: `${dropdownPosition.top}px`,
									left: `${dropdownPosition.left}px`,
									width: `${dropdownPosition.width}px`,
									zIndex: 999999,
									pointerEvents: 'auto',
									overscrollBehavior: 'contain',
								}}
								onMouseDown={(e) => e.stopPropagation()}
								onWheel={(e) => e.stopPropagation()}
							>
								{tagSuggestions.length > 0 ? (
									<>
										<div className="px-2 py-1 text-xs text-muted-foreground border-b border-border sticky top-0 bg-popover z-10">
											{tagInput.trim()
												? 'Suggestions'
												: 'Available tags'}{' '}
											({tagSuggestions.length})
										</div>
										<div className="max-h-[360px] overflow-y-auto">
											{tagSuggestions.map(
												(tag, index) => {
													// Highlight matching text
													const searchIndex = tag
														.toLowerCase()
														.indexOf(
															tagInput
																.toLowerCase()
																.trim()
														);
													const beforeMatch =
														tag.substring(
															0,
															searchIndex
														);
													const match = tag.substring(
														searchIndex,
														searchIndex +
															tagInput.trim()
																.length
													);
													const afterMatch =
														tag.substring(
															searchIndex +
																tagInput.trim()
																	.length
														);

													return (
														<div
															key={tag}
															ref={(el) => {
																if (
																	index ===
																		selectedSuggestionIndex &&
																	el
																) {
																	el.scrollIntoView(
																		{
																			block: 'nearest',
																		}
																	);
																}
															}}
															onMouseDown={(
																e
															) => {
																e.preventDefault(); // Prevent blur from firing
																handleSuggestionClick(
																	tag
																);
															}}
															onMouseEnter={() =>
																setSelectedSuggestionIndex(
																	index
																)
															}
															className={cn(
																'px-2 py-1 cursor-pointer text-sm hover:bg-accent transition-colors',
																index ===
																	selectedSuggestionIndex &&
																	'bg-accent'
															)}
															style={{
																pointerEvents:
																	'auto',
															}}
														>
															{searchIndex >=
															0 ? (
																<>
																	{
																		beforeMatch
																	}
																	<span className="font-semibold">
																		{match}
																	</span>
																	{afterMatch}
																</>
															) : (
																tag
															)}
														</div>
													);
												}
											)}
										</div>
									</>
								) : tagInput.trim() ? (
									<div className="px-2 py-2 text-xs text-muted-foreground">
										Press Enter to create &ldquo;
										{tagInput.trim()}&rdquo;
									</div>
								) : reduxTags.length === 0 ? (
									<div className="px-2 py-2 text-xs text-muted-foreground">
										No tags yet. Type to create your first
										tag.
									</div>
								) : null}
							</div>
						</>,
						document.body
					)}
			</Sheet>

			{/* Customer Lookup Dialog */}
			<CustomerLookup
				customerDialogIsOpen={customerDialogIsOpen}
				handleCustomerDialogIsOpen={setCustomerDialogIsOpen}
				saveCustomer={handleSaveCustomer}
				isActivated={true}
				initialValue=""
			/>
		</>
	);
};

export default PipelineItemSheet;
